import type { DependencyList } from 'react';
import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    useSyncExternalStore,
} from 'react';
import { useStore } from 'react-redux';
import isEqual from 'lodash/isEqual';
import type Luminary from '@lumen5/luminary';
import type {
    Asset,
    AssetType,
    BaseAsset,
    GetAssetType,
    LuminaryObject,
    Scene,
    SceneGroup,
} from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { useForceUpdate } from 'js/src/components/common/hooks/useForceUpdate';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import type { AppDispatch, RootState } from 'js/src/libs/RootState';
import { LuminaryContext, useLuminaryContext } from 'js/src/luminary/luminary-context-provider';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';

export const useLuminaryLoaded = () => {
    const luminary = useLuminaryContext();
    const [luminaryLoaded, setLuminaryLoaded] = useState(false);
    useEffect(() => {
        const setLoadedOnAllImagesRendered = async () => {
            await luminary.onAllSlideImagesRendered();
            setLuminaryLoaded(true);
        };
        setLoadedOnAllImagesRendered().catch(() => {
            // do nothing
        });
    });
    return { luminary, luminaryLoaded };
};

type LuminaryHookOptions = {
    /**
     * The luminary events to subscribe to. If none provided, will subscribe to stateChanged.
     */
    events?: string[];
    /**
     * Optionally, the luminary instance to subscribe to events on. If not provided, the luminary
     * instance from the LuminaryContext will be used.
     */
    luminary?: Luminary;
};

const subscribeToLuminary = (luminary: Luminary, callback: () => void, events?: string[]) => {
    const eventList = events || ['stateChanged'];
    eventList.forEach((event) => {
        luminary.on(event, callback);
    });
    return () => {
        eventList.forEach((event) => {
            luminary.off(event, callback);
        });
    };
};

/**
 * Subscribe to Luminary events and force a React re-render when they occur.
 */
export const useLuminaryEvents = (opts: LuminaryHookOptions) => {
    const luminaryFromContext = useLuminaryContext();
    const luminary = opts.luminary || luminaryFromContext;
    const { forceUpdate } = useForceUpdate();

    useEffect(() => {
        return subscribeToLuminary(luminary, forceUpdate, opts.events);
    }, [luminary, opts?.events, forceUpdate]);
};

export const useLuminaryEffect = (
    effect: (luminary: Luminary) => void,
    deps: DependencyList,
    opts?: LuminaryHookOptions,
) => {
    const luminaryFromContext = useLuminaryContext();
    const luminary = opts?.luminary || luminaryFromContext;
    useEffect(() => {
        return subscribeToLuminary(
            luminary,
            () => {
                effect(luminary);
            },
            opts?.events,
        );
        // We don't want to include the selector function in the dependency list because it may
        // change on every render if it's defined inline. Instead, we rely on the deps list.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [luminary, opts?.events, ...deps]);
};

type LuminaryObjectTypes =
    | LuminaryObject
    | Asset
    | BaseAsset
    | LuminaryObject[]
    | Asset[]
    | BaseAsset[];

type HasLuminaryPropertyValue<T> = T extends object
    ? true extends { [K in keyof T]: T[K] extends LuminaryObjectTypes ? true : false }[keyof T]
        ? true
        : false
    : false;

// Exclude T if it is a Luminary object or contains Luminary object property values
type NotLuminaryClasses<T> = T extends LuminaryObjectTypes
    ? never
    : HasLuminaryPropertyValue<T> extends true
      ? never
      : T;

const UNINITIALIZED = Symbol('UNINITIALIZED');

function isInitialized<T>(value: T | typeof UNINITIALIZED): value is T {
    return value !== UNINITIALIZED;
}

// Note that these are set relatively conservatively, so it's okay if we still need to bump a bit.
// The main goal of these thresholds is to make sure selectors don't do a deep
// equals check on large objects -- since selectors run quite often.
// So before adjusting these limits, try to rewrite the selector to return a smaller object
const SELECTOR_ENTRY_WARN_THRESHOLD = 100;

function countEntries(value: unknown): number {
    if (value === null || value === undefined) {
        return 0;
    }
    if (typeof value !== 'object') {
        return 1;
    }
    if (Array.isArray(value)) {
        return value.reduce((sum, item) => sum + countEntries(item), 0);
    }
    return Object.values(value).reduce((sum: number, v) => sum + countEntries(v), 0);
}

function validateSelectorInDev<T>(value: T) {
    const entryCount = countEntries(value);
    if (entryCount > SELECTOR_ENTRY_WARN_THRESHOLD) {
        // eslint-disable-next-line no-console
        console.warn(
            `[Luminary Selector] Large object returned (${entryCount} entries). ` +
                'Consider selecting only the specific fields you need to avoid performance issues.',
        );
    }
}

/**
 * Uses the luminary font manager to load fonts and figure out what the font name should be
 * for each font url passed in.
 * @param fontUrls a list of font urls
 * @returns a mapping of font urls to font names:
 *     {
 *         [fontUrl]: [fontName],
 *     }
 */
export const useFontUrlsToFontNames = (fontUrls: string[]) => {
    const { updateTrigger, forceUpdate } = useForceUpdate();
    const fontManager = VideoData.getGlobalFontManager();

    const prevFontUrlsRef = useRef(fontUrls);
    const stableFontUrls = useMemo(() => {
        if (isEqual(prevFontUrlsRef.current, fontUrls)) {
            return prevFontUrlsRef.current;
        }
        prevFontUrlsRef.current = fontUrls;
        return fontUrls;
    }, [fontUrls]);

    useEffect(() => {
        const onLoad = () => {
            forceUpdate();
        };

        fontManager.on('loaded', onLoad);

        return () => {
            fontManager.off('loaded', onLoad);
        };
    }, [fontManager, forceUpdate]);

    useEffect(() => {
        stableFontUrls.forEach((fontUrl) => {
            if (!fontManager.getFontBySource(fontUrl)) {
                fontManager.addFont(fontUrl);
            }
        });
    }, [fontManager, stableFontUrls]);

    return useMemo(
        () =>
            stableFontUrls.reduce(
                (acc, fontUrl) => {
                    acc[fontUrl] = fontManager.getFullNameOfLoadedFont(fontUrl);
                    return acc;
                },
                {} as Record<string, string>,
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [fontManager, stableFontUrls, updateTrigger],
    );
};

type LuminarySelectorOptions = LuminaryHookOptions & {
    /**
     * If provided, the selector will only run if the scene group exists and is not destroyed.
     */
    sceneGroupId?: string | null;
    /**
     * If provided, the selector will only run if the scene exists and is not destroyed.
     */
    sceneId?: string | null;
    /**
     * If provided, the selector will only run if the asset exists and is not destroyed.
     */
    assetId?: string;
};

/**
 * A hook that subscribes to luminary changes and returns the value of the selector function.
 * By default, the hook will subscribe to the "stateChanged" event but can be configured to subscribe
 * to other events.
 *
 * You must pass a dependency list to the hook so that it knows when to re-evaluate the selector
 * if a dependency changes. (Dependencies are closure variables used within the selector that
 * come from the outer context and can change value.) An empty array means no dependencies.
 *
 * It's important to not do any state changes or "writes" to luminary within the selector.
 * If you need to do changes to luminary then use useLuminaryOperation.
 *
 * The selector function cannot return a Luminary object (Scene, SceneGroup, Asset, etc.) because
 * those objects are mutable and can change without triggering a re-render. TypeScript will throw
 * a compile-time error if you try to return a Luminary object or an object containing Luminary
 * objects as property values. Instead, extract the specific primitive values you need.
 *
 * Performance note: The selector uses deep comparison (lodash isEqual) to avoid unnecessary
 * re-renders. In dev mode, selectors returning more than SELECTOR_ENTRY_WARN_THRESHOLD entries
 * will log a warning. Select only the specific fields you need.
 */
export const useLuminarySelector = <T>(
    selector: (luminary: Readonly<Luminary>) => NotLuminaryClasses<T>,
    deps: DependencyList,
    opts?: LuminarySelectorOptions,
): NotLuminaryClasses<T> => {
    const luminaryFromContext = useContext(LuminaryContext);
    const luminary = opts?.luminary || luminaryFromContext;
    if (!luminary) {
        throw new Error(
            'useLuminarySelector must be used within a LuminaryContextProvider or be provided a luminary instance',
        );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoizedSelector = useCallback(selector, deps);

    // Keep track of the last computed value for deep comparison.
    // If the getSnapshot returns the same value, it must return the same object reference.
    // Use a symbol to handle the first run -- before we've evaluated the selector.
    const lastValueRef = useRef<NotLuminaryClasses<T> | typeof UNINITIALIZED>(UNINITIALIZED);

    const subscribe = useCallback(
        (onStoreChange: () => void) => {
            return subscribeToLuminary(luminary, onStoreChange, opts?.events);
        },
        [luminary, opts?.events],
    );

    const getSnapshot = useCallback((): NotLuminaryClasses<T> => {
        const lastValue = lastValueRef.current;
        const shouldRun = shouldRunSelector(
            luminary,
            opts?.sceneGroupId,
            opts?.sceneId,
            opts?.assetId,
        );

        if (isInitialized(lastValue) && !shouldRun) {
            return lastValue;
        }

        const newValue = memoizedSelector(luminary);
///////////////////
////////////////////////////////////////
//////////////////

        // Use deep comparison to avoid unnecessary re-renders for equivalent values.
        if (isInitialized(lastValue) && isEqual(lastValue, newValue)) {
            return lastValue;
        }

        lastValueRef.current = newValue;
        return newValue;
    }, [luminary, memoizedSelector, opts?.sceneGroupId, opts?.sceneId, opts?.assetId]);

    return useSyncExternalStore(subscribe, getSnapshot);
};

/**
 * If the selector filters for a specific scene group, scene, or asset, then we only
 * want to run the selector if the filtered object exists and is not destroyed.
 */
function shouldRunSelector(
    luminary: Luminary,
    sceneGroupId?: string | null,
    sceneId?: string | null,
    assetId?: string,
): boolean {
    let sceneGroup: SceneGroup | undefined;
    let scene: Scene | undefined;
    if (sceneGroupId) {
        sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup || sceneGroup.destroyed) {
            return false;
        }
    }
    if (sceneId) {
        scene = luminary.getSceneById(sceneId);
        if (!scene || scene.destroyed) {
            return false;
        }
    }
    if (assetId) {
        const asset = sceneGroup ? sceneGroup.getAssetById(assetId) : scene?.getAssetById(assetId);
        if (!asset || asset.destroyed) {
            return false;
        }
    }
    return true;
}

type SceneGroupSelectorOptionsStrict = LuminaryHookOptions & {
    sceneGroupId: string;
    /**
     * When true, returns undefined instead of throwing if the scene group cannot be found.
     * This changes the return type to include undefined (T | undefined).
     * When false or omitted (default), throws an error if lookup fails.
     */
    returnUndefinedIfMissing?: false;
};

type SceneGroupSelectorOptionsReturnUndefined = LuminaryHookOptions & {
    /**
     * When true, returns undefined instead of throwing if the scene group cannot be found.
     * This changes the return type to include undefined (T | undefined).
     * When false or omitted (default), throws an error if lookup fails.
     * When true, sceneGroupId can be null.
     */
    sceneGroupId: string | null;
    returnUndefinedIfMissing: true;
};

/**
 * A convenience luminary selector for selecting a scene group by id.
 *
 * If the scene group cannot be found:
 * - With returnUndefinedIfMissing: true: returns undefined (return type is T | undefined)
 * - Without returnUndefinedIfMissing (default): throws an error
 *
 * Refer to useLuminarySelector for more details.
 */
// Overload 1: returnUndefinedIfMissing: true - returns T | undefined
export function useLuminarySceneGroupSelector<T>(
    selector: (sceneGroup: Readonly<SceneGroup>) => NotLuminaryClasses<T>,
    deps: DependencyList,
    opts: SceneGroupSelectorOptionsReturnUndefined,
): NotLuminaryClasses<T> | undefined;

// Overload 2: Without returnUndefinedIfMissing (strict mode) - returns T, throws if not found
export function useLuminarySceneGroupSelector<T>(
    selector: (sceneGroup: Readonly<SceneGroup>) => NotLuminaryClasses<T>,
    deps: DependencyList,
    opts: SceneGroupSelectorOptionsStrict,
): NotLuminaryClasses<T>;

// Implementation
export function useLuminarySceneGroupSelector<T>(
    selector: (sceneGroup: Readonly<SceneGroup>) => NotLuminaryClasses<T>,
    deps: DependencyList,
    opts: SceneGroupSelectorOptionsStrict | SceneGroupSelectorOptionsReturnUndefined,
): NotLuminaryClasses<T> | undefined {
    return useLuminarySelector<T | undefined>(
        (luminary) => {
            if (!opts.sceneGroupId) {
                if (opts.returnUndefinedIfMissing) {
                    return undefined;
                }
                ErrorReporter.setContext('scene group selector', { opts });
                throw new Error('useLuminarySceneGroupSelector: sceneGroupId is required');
            }
            const sceneGroup = luminary.getSceneGroupById(opts.sceneGroupId);
            if (!sceneGroup || sceneGroup.destroyed) {
                if (opts.returnUndefinedIfMissing) {
                    return undefined;
                }
                ErrorReporter.setContext('scene group selector', {
                    opts,
                    destroyed: sceneGroup?.destroyed,
                });
                throw new Error('useLuminarySceneGroupSelector: scene group not found');
            }
            return selector(sceneGroup);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [opts.sceneGroupId, ...deps],
        opts,
    );
}

type SceneSelectorOptionsStrict = LuminaryHookOptions & {
    sceneId: string;
    /**
     * When true, returns undefined instead of throwing if the scene cannot be found.
     * This changes the return type to include undefined (T | undefined).
     * When false or omitted (default), throws an error if lookup fails.
     */
    returnUndefinedIfMissing?: false;
};

type SceneSelectorOptionsReturnUndefined = LuminaryHookOptions & {
    /**
     * When true, returns undefined instead of throwing if the scene cannot be found.
     * This changes the return type to include undefined (T | undefined).
     * When false or omitted (default), throws an error if lookup fails.
     * When true, sceneId can be null.
     */
    sceneId: string | null;
    returnUndefinedIfMissing: true;
};

/**
 * A convenience luminary selector for selecting a scene by id.
 *
 * If the scene cannot be found:
 * - With returnUndefinedIfMissing: true: returns undefined (return type is T | undefined)
 * - Without returnUndefinedIfMissing (default): throws an error
 *
 * Refer to useLuminarySelector for more details.
 */
// Overload 1: returnUndefinedIfMissing: true - returns T | undefined
export function useLuminarySceneSelector<T>(
    selector: (scene: Readonly<Scene>) => NotLuminaryClasses<T>,
    deps: DependencyList,
    opts: SceneSelectorOptionsReturnUndefined,
): NotLuminaryClasses<T> | undefined;

// Overload 2: Without returnUndefinedIfMissing (strict mode) - returns T, throws if not found
export function useLuminarySceneSelector<T>(
    selector: (scene: Readonly<Scene>) => NotLuminaryClasses<T>,
    deps: DependencyList,
    opts: SceneSelectorOptionsStrict,
): NotLuminaryClasses<T>;

// Implementation
export function useLuminarySceneSelector<T>(
    selector: (scene: Readonly<Scene>) => NotLuminaryClasses<T>,
    deps: DependencyList,
    opts: SceneSelectorOptionsStrict | SceneSelectorOptionsReturnUndefined,
): NotLuminaryClasses<T> | undefined {
    return useLuminarySelector<T | undefined>(
        (luminary) => {
            if (!opts.sceneId) {
                if (opts.returnUndefinedIfMissing) {
                    return undefined;
                }
                ErrorReporter.setContext('scene selector', { opts });
                throw new Error('useLuminarySceneSelector: sceneId is required');
            }
            const scene = luminary.getSceneById(opts.sceneId);
            if (!scene || scene.destroyed) {
                if (opts.returnUndefinedIfMissing) {
                    return undefined;
                }
                ErrorReporter.setContext('scene selector', { opts, destroyed: scene?.destroyed });
                throw new Error('useLuminarySceneSelector: scene not found');
            }
            return selector(scene);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [opts.sceneId, ...deps],
        opts,
    );
}

type AssetSelectorOptionsBase<T extends AssetType> = LuminaryHookOptions & {
    sceneGroupId?: string;
    sceneId?: string;
    assetId: string;
    assetType: T;
} & ({ sceneGroupId: string; sceneId?: never } | { sceneGroupId?: never; sceneId: string });

type AssetSelectorOptionsStrict<T extends AssetType> = AssetSelectorOptionsBase<T> & {
    /**
     * When true, returns undefined instead of throwing if the scene, group, or asset cannot be found.
     * This changes the return type to include undefined (T | undefined).
     * When false or omitted (default), throws an error if lookup fails.
     */
    returnUndefinedIfMissing?: false;
};

type AssetSelectorOptionsReturnUndefined<T extends AssetType> = AssetSelectorOptionsBase<T> & {
    /**
     * When true, returns undefined instead of throwing if the scene, group, or asset cannot be found.
     * This changes the return type to include undefined (T | undefined).
     * When false or omitted (default), throws an error if lookup fails.
     */
    returnUndefinedIfMissing: true;
};

/**
 * A convenience luminary selector for selecting an asset by id. You need to also provide either
 * a sceneGroupId or sceneId to indicate which scene or scene group to look in.
 *
 * If the scene/group/asset cannot be found:
 * - With returnUndefinedIfMissing: true: returns undefined (return type is T | undefined)
 * - Without returnUndefinedIfMissing (default): throws an error
 *
 * Refer to useLuminarySelector for more details.
 */
// Overload 1: returnUndefinedIfMissing: true - returns T | undefined
export function useLuminaryAssetSelector<T, A extends AssetType>(
    selector: (asset: GetAssetType<A>) => NotLuminaryClasses<T>,
    deps: DependencyList,
    opts: AssetSelectorOptionsReturnUndefined<A>,
): NotLuminaryClasses<T> | undefined;

// Overload 2: Without returnUndefinedIfMissing (strict mode) - returns T, throws if not found
export function useLuminaryAssetSelector<T, A extends AssetType>(
    selector: (asset: GetAssetType<A>) => NotLuminaryClasses<T>,
    deps: DependencyList,
    opts: AssetSelectorOptionsStrict<A>,
): NotLuminaryClasses<T>;

// Implementation
export function useLuminaryAssetSelector<T, A extends AssetType>(
    selector: (asset: GetAssetType<A>) => NotLuminaryClasses<T>,
    deps: DependencyList,
    opts: AssetSelectorOptionsBase<A> & { returnUndefinedIfMissing?: boolean },
): NotLuminaryClasses<T> | undefined {
    return useLuminarySelector<T | undefined>(
        (luminary) => {
            let sceneOrGroup: SceneGroup | Scene | undefined;
            if (opts.sceneGroupId) {
                sceneOrGroup = luminary.getSceneGroupById(opts.sceneGroupId);
            } else if (opts.sceneId) {
                sceneOrGroup = luminary.getSceneById(opts.sceneId);
            }
            if (!sceneOrGroup) {
                if (opts.returnUndefinedIfMissing) {
                    return undefined;
                }
                ErrorReporter.setContext('asset selector', { opts });
                throw new Error('useLuminaryAssetSelector: scene or group not found');
            }

            const asset = sceneOrGroup.getAssetById(opts.assetId);
            if (!asset || asset.destroyed) {
                if (opts.returnUndefinedIfMissing) {
                    return undefined;
                }
                ErrorReporter.setContext('asset selector', { opts, destroyed: asset?.destroyed });
                throw new Error('useLuminaryAssetSelector: asset not found');
            }

            if (asset.classType !== opts.assetType) {
                if (opts.returnUndefinedIfMissing) {
                    return undefined;
                }
                ErrorReporter.setContext('asset selector', { opts, type: asset.classType });
                throw new Error('useLuminaryAssetSelector: asset type mismatch');
            }

            return selector(asset as GetAssetType<A>);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [opts.sceneGroupId, opts.sceneId, opts.assetId, ...deps],
        opts,
    );
}

export const _luminaryOperationSymbol = Symbol.for('luminaryOperationSymbol');

type MakeOperationNotCallable<T> = {
    [_luminaryOperationSymbol]: T;
};

export type MakeOperationsCallable<NCO extends RecordOfNotCallableOperations> =
    keyof NCO extends never
        ? NonNullable<unknown>
        : {
              [O in keyof NCO extends `${infer N}Operation`
                  ? N
                  : never]: NCO[`${O}Operation`] extends NotCallableOperation<infer iP, infer iR>
                  ? (...params: iP) => iR
                  : never;
          };

export type MakeOperationCallable<NCO extends NotCallableOperation<any, any>> =
    NCO extends NotCallableOperation<infer iP, infer iR> ? (...params: iP) => iR : never;

type LuminaryOverrideArg = { luminary: Luminary };
type LuminaryListOverrideArg = { luminaryList: Luminary[] };

type GetNonCallableOperationParams<OP> = OP extends NotCallableOperation<infer P, any> ? P : never;
type GetNonCallableOperationReturn<OP> = OP extends NotCallableOperation<any, infer R> ? R : never;

export type OperationCall = {
    <OP>(op: OP, ...args: GetNonCallableOperationParams<OP>): GetNonCallableOperationReturn<OP>;
    <OP>(
        op: OP,
        ...args: [...GetNonCallableOperationParams<OP>, { luminary: Luminary }]
    ): GetNonCallableOperationReturn<OP>;
    <OP>(
        op: OP,
        ...args: [...GetNonCallableOperationParams<OP>, { luminaryList: Luminary[] }]
    ): GetNonCallableOperationReturn<OP>;
};

type CallableOperation<P extends Array<any>, R> = (
    config: {
        dispatch: AppDispatch;
        luminary: Luminary;
        getState: GetRootState;
        call: OperationCall;
    },
    ...params: P
) => R;

export type NotCallableOperation<P extends Array<any>, R> = MakeOperationNotCallable<
    CallableOperation<P, R>
>;

export type RecordOfNotCallableOperations = {
    [O: `${string}Operation`]: NotCallableOperation<any, any>;
};

/**
 * Creates a luminary operation. This is used to create operations that cannot be called directly,
 * but instead must be called through useLuminaryOperations.
 *
 * @param callback - required function that defines the luminary operation
 * @param requiredOperations - optional object of other luminary operations which are to be used by this one.
 */
export function createLuminaryOperation<Params extends Array<any>, Ret>(
    callback: CallableOperation<Params, Ret>,
): MakeOperationNotCallable<CallableOperation<Params, Ret>> {
    return {
        [_luminaryOperationSymbol]: callback,
    };
}

export function createCall<Params extends any[], Ret, T extends NotCallableOperation<Params, Ret>>({
    luminary,
    dispatch,
    getState,
}: {
    luminary: Luminary;
    dispatch: AppDispatch;
    getState: GetRootState;
}): {
    (notCallableOperation: T, ...args: [...Params]): Ret;
    (notCallableOperation: T, ...args: [...Params, LuminaryOverrideArg]): Ret;
    (notCallableOperation: T, ...args: [...Params, LuminaryListOverrideArg]): Ret[];
} {
    return ((notCallableOperation, ...args) => {
        let argsToPass: Params;
        let luminaryInstanceToPass: Luminary;
        let luminaryListToPass: Luminary[] | undefined;
        if (
            args.length &&
            args[args.length - 1] &&
            ((args[args.length - 1] as LuminaryOverrideArg).luminary ||
                (args[args.length - 1] as LuminaryListOverrideArg).luminaryList)
        ) {
            argsToPass = args.slice(0, -1) as Params;
            luminaryInstanceToPass = (args[args.length - 1] as LuminaryOverrideArg).luminary;
            luminaryListToPass = (args[args.length - 1] as LuminaryListOverrideArg).luminaryList;
        } else {
            argsToPass = args as Params;
            luminaryInstanceToPass = luminary;
            luminaryListToPass = undefined;
        }

        const callableOperation = notCallableOperation[_luminaryOperationSymbol];
        if (luminaryListToPass) {
            return luminaryListToPass.map((luminaryInstance) =>
                callableOperation(
                    {
                        luminary: luminaryInstance,
                        dispatch,
                        getState,
                        call: createCall({
                            luminary: luminaryInstance,
                            dispatch,
                            getState,
                        }),
                    },
                    ...argsToPass,
                ),
            );
        }

        return callableOperation(
            {
                luminary: luminaryInstanceToPass,
                dispatch,
                getState,
                call: createCall({
                    luminary: luminaryInstanceToPass,
                    dispatch,
                    getState,
                }),
            },
            ...argsToPass,
        );
    }) as {
        (notCallableOperation: T, ...args: [...Params]): Ret;
        (notCallableOperation: T, ...args: [...Params, LuminaryOverrideArg]): Ret;
        (notCallableOperation: T, ...args: [...Params, LuminaryListOverrideArg]): Ret[];
    };
}

export function makeOperationsCallable<T extends RecordOfNotCallableOperations>(
    nonCallableOperations: T,
    luminary: Luminary,
    dispatch: AppDispatch,
    getState: GetRootState,
): MakeOperationsCallable<T> {
    return Object.fromEntries(
        Object.entries(nonCallableOperations).map(([name, notCallableOperation]) => [
            name.replace('Operation', ''),
            (...args: any) => {
                const callableOperation = notCallableOperation[_luminaryOperationSymbol];
                return callableOperation(
                    {
                        luminary,
                        dispatch,
                        getState,
                        call: createCall({ luminary, dispatch, getState }),
                    },
                    ...args,
                );
            },
        ]),
    ) as MakeOperationsCallable<T>;
}

type LuminaryOperationOptions = {
    /**
     * Optionally, the luminary instance to perform actions on. If not provided, the luminary
     * instance from the LuminaryContext will be used.
     */
    luminary?: Luminary;
};

/**
 * A hook for using luminary operations. This hook is similar to useLuminarySelector, but instead
 * of reading a value from luminary, it allows you to call a function (or perform a write action)
 * on luminary.
 * It also passes the redux state and dispatch function to the operation, so you can
 * read and write to the redux store as well.
 *
 * Operations passed to this hook must be created with createLuminaryOperation. This is to ensure
 * that operations are not called directly, but instead are called through this hook.
 *
 * This will return an object of callable operations (functions) that have the same name as the
 * operations passed in, but without the "Operation" suffix.
 */
export const useLuminaryOperations = <T extends RecordOfNotCallableOperations>(
    operations: T,
    opts?: LuminaryOperationOptions,
): MakeOperationsCallable<T> => {
    const luminaryFromContext = useContext(LuminaryContext);
    const luminary = opts?.luminary || luminaryFromContext;
    if (!luminary) {
        throw new Error(
            'useLuminaryOperations must be used within a LuminaryContextProvider or be provided a luminary instance',
        );
    }
    const store = useStore<RootState>();

    return useMemo(() => {
        return makeOperationsCallable(operations, luminary, store.dispatch, store.getState);
        // operations will never change, so we don't need to recompute this, however since
        // we pass in operations as an object, it will always be a new object each render, so
        // we need to disable the exhaustive deps rule.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [luminary, store.dispatch, store.getState]);
};
